import * as React from 'react'

// Plain text input that accepts a typed MM/DD/YYYY date and commits to a Date
// object when the typed string is a complete, valid date. The input keeps
// whatever the user is typing — including incomplete values — so they can type
// a date in one action without the input fighting them. When the underlying
// Date changes externally (e.g. via a paired calendar widget) the input
// resyncs.

// Normalise a date-ish value (Date, moment.Moment, ISO string, or null) into a
// native Date. Lets callers continue to use moment objects with components
// that expect a Date without forcing a codebase-wide moment removal.
export function toDate(value: any): Date | null {
  if (!value) return null
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
  if (typeof value.toDate === 'function') {
    const d = value.toDate()
    return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null
  }
  if (typeof value === 'string') {
    // SQL `date` columns serialize as bare YYYY-MM-DD. `new Date(yyyymmdd)`
    // parses those as UTC midnight, which renders as the previous day for
    // users west of UTC. moment("YYYY-MM-DD") parsed as local midnight, so we
    // match that here to avoid an off-by-one regression.
    const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
    if (ymd) {
      const parsed = new Date(parseInt(ymd[1], 10), parseInt(ymd[2], 10) - 1, parseInt(ymd[3], 10))
      return Number.isNaN(parsed.getTime()) ? null : parsed
    }
    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }
  return null
}

export function formatDateForInput(value: Date | null | undefined | any): string {
  const date = toDate(value)
  if (!date) return ''
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const year = date.getFullYear()
  return `${month}/${day}/${year}`
}

export function parseInputDate(value: string): Date | null {
  if (!value) return null
  // Accept MM/DD/YYYY or M/D/YYYY. Year must be exactly 4 digits.
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value.trim())
  if (!match) return null
  const month = parseInt(match[1], 10)
  const day = parseInt(match[2], 10)
  const year = parseInt(match[3], 10)
  if (month < 1 || month > 12) return null
  if (day < 1 || day > 31) return null
  // Round-trip through Date to reject impossible dates like 02/30/2024 (which
  // Date would silently roll over to March).
  const parsed = new Date(year, month - 1, day)
  if (parsed.getFullYear() !== year || parsed.getMonth() !== month - 1 || parsed.getDate() !== day) {
    return null
  }
  return parsed
}

interface DateInputProps {
  ariaLabel: string;
  className?: string;
  date: Date | null;
  setDate: (date: Date | null) => void;
}

export const DateInput: React.FC<DateInputProps> = ({ ariaLabel, className, date, setDate }) => {
  const [inputValue, setInputValue] = React.useState(formatDateForInput(date))
  // Track the last externally-driven date so we know when to overwrite the
  // user's typed text. If `date` changes from outside (calendar click) we
  // sync; if it changed because the user typed a complete date, the
  // inputValue already matches and the effect is a no-op.
  const lastSyncedRef = React.useRef<Date | null>(date)
  React.useEffect(() => {
    if (lastSyncedRef.current !== date) {
      lastSyncedRef.current = date
      setInputValue(formatDateForInput(date))
    }
  }, [date])

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const next = e.target.value
    setInputValue(next)
    if (!next) {
      lastSyncedRef.current = null
      setDate(null)
      return
    }
    const parsed = parseInputDate(next)
    if (parsed) {
      lastSyncedRef.current = parsed
      setDate(parsed)
    } else if (date) {
      // Non-empty input that doesn't parse (mid-typing or invalid like
      // 02/30/2024). Clear the committed date so Apply gets disabled and the
      // form can't submit a stale value while the input visibly shows garbage.
      lastSyncedRef.current = null
      setDate(null)
    }
  }

  return (
    <input
      aria-label={ariaLabel}
      className={className}
      inputMode="numeric"
      onChange={handleChange}
      placeholder="MM/DD/YYYY"
      type="text"
      value={inputValue}
    />
  )
}

export default DateInput
